import { useEffect, useState } from "react";
import { audio } from "../../lib/audio";
import { dd, crits as mkCrits, MISSION_ORDER, type DdCrit } from "./state";
import { Btn, Bubble, Choice, Consequence, FixTag, KeepInstead, Maya, MissFooter, MissionHead, MissionResult, Seg, Todo, Win, useGuide, usePrefetch, type DdOpt } from "./ui";
import type { StageProps } from "./types";
import { useSim } from "../../state/store";
import { DD_QUIZ_TOPIC } from "../../scenario/the-1700-release.content";
import { getHostConfig } from "../../lib/hostConfig";

/* ================================================================== D8 test matrix (S4) + CI (S6) */
interface D8Content {
  sam: string;
  behaviors: DdOpt[];
  cases: { id: string; who: string; op: string; good: string; group: string }[];
  stages: { id: string; label: string; good: string }[];
  profiles: DdOpt[];
  request: string;
  maya: string;
  crits: { id: string; label: string }[];
}
const BEH_SHORT: Record<string, string> = { allow: "Allow", deny: "Deny", literal: "Literal data", reject: "Reject / cap" };

export function D8Matrix({ cfg, finish, onGuideChange }: StageProps) {
  const c = cfg.content as D8Content;
  const [ans, setAns] = useState<Record<string, string>>({});
  const [ran, setRan] = useState(false);
  useGuide(onGuideChange, cfg.guide, ran ? "ran" : "matrix");
  const ready = c.cases.every((k) => ans[k.id]);
  const short = c.behaviors.map((b) => ({ id: b.id, label: BEH_SHORT[b.id] ?? b.label }));
  return (
    <Win app={"Test lab \u00b7 Behaviour checks"} tag="S4" clock={cfg.clock}>
      <div className="dd-pad dd-matrix">
        <div className="dd-matrix-h">
          <MissionHead n={8} time="14:30" title="Build the checks the demo forgot" />
          <Bubble p={cfg.people.sam}>{c.sam}</Bubble>
        </div>
        <div className="dd-legend">
          {c.behaviors.map((b) => (
            <span key={b.id}><b>{BEH_SHORT[b.id]}</b> {b.label.split(":")[1] ?? ""}</span>
          ))}
        </div>
        <table className="dd-table dd-mtable">
          <thead><tr><th>Identity / input</th><th>Operation</th><th>Required behavior</th>{ran && <th>Result</th>}</tr></thead>
          <tbody>
            {c.cases.map((k) => (
              <tr key={k.id} className={!ran && !ans[k.id] ? "is-todo" : ""}>
                <td>{k.who}{!ran && !ans[k.id] && <Todo />}</td>
                <td>{k.op}</td>
                <td><Seg small options={short} value={ans[k.id]} onChange={(v) => setAns((m) => ({ ...m, [k.id]: v }))} disabled={ran} /></td>
                {ran && (
                  <td className={ans[k.id] === k.good ? "is-pass" : "is-fail"}>
                    {ans[k.id] === k.good ? "\u2713 Pass" : `\u2715 App: ${BEH_SHORT[k.good]}`}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="dd-actions">
          {ran ? (
            <>
              <span className="dd-muted">Candidate R7 {"\u00b7"} config cfg-12 {"\u00b7"} suite 4.8.3 {"\u00b7"} 14:31</span>
              <Btn primary pulse onClick={() => { dd.set({ d8: { answers: ans, ranAt: "14:31" } }); finish("continue"); }}>Open the CI pipeline</Btn>
            </>
          ) : (
            <>
              <span className={`dd-decided${ready ? " is-all" : ""}`}><b>{c.cases.filter((k) => ans[k.id]).length}</b> of {c.cases.length} set{ready ? " \u2713" : ""}</span>
              <Btn primary pulse={ready} disabled={!ready} onClick={() => { setRan(true); audio.ui("message"); }}>Run harness</Btn>
            </>
          )}
        </div>
      </div>
    </Win>
  );
}

export function D8Ci({ cfg, finish, onGuideChange, rec }: StageProps) {
  const c = cfg.content as D8Content;
  const [ans, setAns] = useState<Record<string, string>>(() => ({ ...(dd.get().d8?.answers ?? {}) }));
  const [prof, setProf] = useState<Record<string, string>>({});
  const [stage, setStage] = useState<"ci" | "result" | "fix" | "fixed">("ci");
  const [first, setFirst] = useState<DdCrit[] | null>(null);
  const [explained, setExplained] = useState(false);
  const [fixMsg, setFixMsg] = useState("");
  useGuide(onGuideChange, cfg.guide, stage === "fixed" ? "result" : stage);
  usePrefetch([c.maya]);
  const ready = c.stages.every((s) => prof[s.id]);
  const grp = (g: string) => c.cases.filter((k) => k.group === g).every((k) => ans[k.id] === k.good);
  const ciOk = c.stages.every((s) => prof[s.id] === s.good);
  const fix = stage === "fix";
  const short = c.behaviors.map((b) => ({ id: b.id, label: BEH_SHORT[b.id] ?? b.label }));
  const submit = () => {
    if (fix) {
      if (!ciOk || c.cases.some((k) => ans[k.id] !== k.good)) {
        setFixMsg("Some items still need a fix.");
        audio.ui("error");
        return;
      }
      dd.set({ d8: { answers: ans, ranAt: "14:52" } });
      dd.log("d8", "Completed the negative tests and separated test runners from deployment privileges.", "corrected");
      audio.ui("success");
      setStage("fixed");
      return;
    }
    const cr = mkCrits(c.crits, { allow: grp("allow"), deny: grp("deny"), boundary: grp("boundary"), input: grp("input"), ci: ciOk });
    const kept = dd.commitFirst("d8", cr, `${c.cases.filter((k) => ans[k.id] === k.good).length} of ${c.cases.length} behaviors right, pipeline ${ciOk ? "separated" : "still shares privileges"}`);
    setFirst(kept);
    rec("dd_d8_first", `${dd.missionScore("d8")}/10`);
    audio.ui(ciOk ? "success" : "error");
    setStage("result");
  };
  const allOk = !!first && first.every((x) => x.ok);
  const wrongCases = c.cases.filter((k) => ans[k.id] !== k.good);
  return (
    <Win app={"Build and release \u00b7 Pipeline"} tag="S6" clock={cfg.clock}>
      {(stage === "ci" || fix) && (
        <div className="dd-pad">
          <MissionHead n={8} time="14:40" title="Protect the test and release environments" />
          <Consequence tone="warn" title="Pending permission request">{c.request}</Consequence>
          <div className="dd-stages">
            {c.stages.map((s) => (
              <div key={s.id} className={`dd-stage${fix && prof[s.id] !== s.good ? " is-fix" : !prof[s.id] ? " is-todo" : ""}`}>
                <b>{s.label}{fix && prof[s.id] !== s.good ? <FixTag /> : !prof[s.id] ? <Todo /> : null}</b>
                <Seg small options={c.profiles} value={prof[s.id]} onChange={(v) => setProf((m) => ({ ...m, [s.id]: v }))} />
              </div>
            ))}
          </div>
          {fix && wrongCases.length > 0 && (
            <div className="dd-fixcases">
              <b className="dd-sub">Test expectations to fix</b>
              {wrongCases.map((k) => (
                <div key={k.id} className="dd-fixcase">
                  <span>{k.who} {"\u00b7"} {k.op}</span>
                  <Seg small options={short} value={ans[k.id]} onChange={(v) => setAns((m) => ({ ...m, [k.id]: v }))} />
                </div>
              ))}
            </div>
          )}
          <div className="dd-actions">
            {fixMsg && fix && <span className="dd-warnline">{fixMsg}</span>}
            <span className="dd-muted">Agent instructions and a prompt classifier do not enforce this separation; the pipeline does.</span>
            {fix && <KeepInstead onClick={() => { setExplained(true); setFixMsg(""); setStage("result"); }} />}
            <Btn primary pulse={ready} disabled={!ready} onClick={submit}>{fix ? "Save again" : "Save pipeline"}</Btn>
          </div>
        </div>
      )}
      {(stage === "result" || stage === "fixed") && first && (
        <MissionResult
          n={8}
          time="14:55"
          title="Verified candidate checks"
          crits={first}
          outcomes={[
            stage === "fixed" || allOk
              ? { tone: "ok", title: "Checks recorded for candidate R7", body: "Pull-request jobs run isolated without secrets; deployment uses its own identity and approval." + (stage === "fixed" ? " Corrected during training." : "") }
              : {
                  tone: "warn",
                  title: "Gaps remain",
                  body: (
                    <>
                      {!ciOk ? "Untrusted code could still inherit production privileges. " : ""}
                      {wrongCases.length ? `${wrongCases.length} expected behaviors do not match the requirements.` : ""}
                    </>
                  ),
                },
          ]}
          maya={{ person: cfg.people.maya, text: c.maya, speak: stage === "result" }}
          footer={stage === "fixed" ? (
              <Btn primary pulse onClick={() => { dd.master("d8", true); finish("continue"); }}>Continue</Btn>
            ) : (
              <MissFooter
                allOk={allOk}
                explained={explained}
                onExplain={() => { setExplained(true); audio.ui("docOpen"); }}
                onRetry={() => { setFixMsg(""); setStage("fix"); }}
                onContinue={() => { if (allOk) dd.master("d8", false); else dd.acknowledge("d8"); finish("continue"); }}
                retryLabel="Try again to improve your score"
              />
            )}
        />
      )}
    </Win>
  );
}

/* ================================================================== D9 incident (S6) */
interface D9Content {
  alert: string; sam: string; evidence: string[];
  classify: DdOpt[];
  lanes: DdOpt[];
  actions: { id: string; label: string; good: string }[];
  maya: string;
  crits: { id: string; label: string }[];
}

export function D9({ cfg, finish, onGuideChange, rec }: StageProps) {
  const c = cfg.content as D9Content;
  const [cls, setCls] = useState<string>();
  const [clsFirst, setClsFirst] = useState<string | null>(null);
  const [lane, setLane] = useState<Record<string, string>>({});
  const [stage, setStage] = useState<"classify" | "board" | "result" | "fix" | "fixed">("classify");
  const [first, setFirst] = useState<DdCrit[] | null>(null);
  const [explained, setExplained] = useState(false);
  const [fixMsg, setFixMsg] = useState("");
  useGuide(onGuideChange, cfg.guide, stage === "fixed" ? "result" : stage);
  usePrefetch([c.maya]);
  useEffect(() => {
    audio.ui("alert");
  }, []);
  const ready = c.actions.every((a) => lane[a.id]);
  const ok = (id: string) => lane[id] === c.actions.find((a) => a.id === id)!.good;
  const fix = stage === "fix";
  const submit = () => {
    if (fix) {
      if (cls !== "precise" || !c.actions.every((a) => ok(a.id))) {
        setFixMsg("Some actions still need a fix.");
        audio.ui("error");
        return;
      }
      dd.log("d9", "Revoked and replaced the staging credential; recovery verified.", "corrected");
      audio.ui("success");
      setStage("fixed");
      return;
    }
    const cr = mkCrits(c.crits, {
      classify: clsFirst === "precise",
      contain: ok("notify") && ok("contain"),
      revoke: ok("revoke"),
      evidence: ok("preserve") && ok("scope") && ok("paste") && ok("wipelogs"),
      recover: ok("replace") && ok("clean") && ok("logging") && ok("validate") && ok("deleteline") && ok("wait"),
    });
    const kept = dd.commitFirst("d9", cr, `Exposure stated as: ${c.classify.find((x) => x.id === clsFirst)?.label}; revocation ${ok("revoke") ? "arranged at once" : "not arranged at once"}`);
    setFirst(kept);
    rec("dd_d9_first", `${dd.missionScore("d9")}/10`);
    dd.log("d9", "Colleague's diagnostic artifact exposed a staging token (independent of your decisions).", "note");
    dd.set({ candidate: "R8" });
    audio.ui(ok("revoke") ? "success" : "error");
    setStage("result");
  };
  const allOk = !!first && first.every((x) => x.ok);
  const revoked = stage === "fixed" || ok("revoke");
  return (
    <Win app={"Incident console \u00b7 Staging"} tag="S6" clock={cfg.clock}>
      {stage === "classify" && (
        <div className="dd-split">
          <aside className="dd-side">
            <MissionHead n={9} time="15:40" title="A secret in yesterday's artifact" />
            <div className="dd-alert" role="alert">{c.alert}</div>
            <Bubble p={cfg.people.sam}>{c.sam}</Bubble>
          </aside>
          <section className="dd-main">
            <b className="dd-sub">Evidence</b>
            <ul className="dd-evlist">{c.evidence.map((e) => <li key={e}>{e}</li>)}</ul>
            <b className="dd-sub">What is actually confirmed?</b>
            <Choice options={c.classify} value={cls} onChange={setCls} />
            <div className="dd-actions">
              <Btn primary pulse={!!cls} disabled={!cls} onClick={() => { setClsFirst(cls!); setStage("board"); audio.click(); }}>Open the response board</Btn>
            </div>
          </section>
        </div>
      )}
      {(stage === "board" || fix) && (
        <div className="dd-pad">
          <div className="dd-board-h">
            <b>Response board</b>
            <span className="dd-muted">Notification, containment, revocation and evidence can overlap. There is no single correct order.</span>
          </div>
          {fix && cls !== "precise" && (
            <div className="dd-fixcase">
              <span>What is confirmed? <FixTag /></span>
              <Seg small options={c.classify} value={cls} onChange={setCls} />
            </div>
          )}
          <div className="dd-lanes">
            {c.actions.map((a) => (
              <div key={a.id} className={`dd-laneRow${fix && !ok(a.id) ? " is-fix" : !lane[a.id] ? " is-todo" : ""}`}>
                <span>{a.label}{fix && !ok(a.id) ? <FixTag /> : !lane[a.id] ? <Todo /> : null}</span>
                <Seg small options={c.lanes} value={lane[a.id]} onChange={(v) => setLane((m) => ({ ...m, [a.id]: v }))} />
              </div>
            ))}
          </div>
          <div className="dd-actions">
            {fixMsg && fix && <span className="dd-warnline">{fixMsg}</span>}
            {!fix && <span className={`dd-decided${ready ? " is-all" : ""}`}><b>{c.actions.filter((a) => lane[a.id]).length}</b> of {c.actions.length} placed{ready ? " \u2713" : ""}</span>}
            {fix && <KeepInstead onClick={() => { setExplained(true); setFixMsg(""); setStage("result"); }} />}
            <Btn primary pulse={ready} disabled={!ready} onClick={submit}>{fix ? "Start again" : "Start response"}</Btn>
          </div>
        </div>
      )}
      {(stage === "result" || stage === "fixed") && first && (
        <MissionResult
          n={9}
          time="16:10"
          title="Incident and recovery record"
          crits={first}
          outcomeLabel="Recovery record"
          outcomeSub="Credential owner panel \u00b7 Export service team"
          more={
            <div className="dd-owner">
              <span className="dd-kicker">Credential owner panel {"\u00b7"} Export service team</span>
              <div className={revoked ? "is-ok" : "is-risk"}>{revoked ? "\u2713 Old credential revoked" : "\u2715 Credential active"}</div>
              <div className={revoked ? "is-ok" : "is-muted"}>{revoked ? "\u2713 Replacement in managed secret storage \u00b7 Staging consumer healthy" : "Deleting the visible line did not invalidate the token."}</div>
              <div className="is-muted">Impact review: open (no misuse confirmed; investigation continues)</div>
              <div className="is-muted">Fix staged: candidate R7 {"\u2192"} R8 (unsafe debug logging removed)</div>
            </div>
          }
          maya={{ person: cfg.people.maya, text: c.maya, speak: stage === "result" }}
          footer={stage === "fixed" ? (
              <Btn primary pulse onClick={() => { dd.master("d9", true); finish("continue"); }}>Continue</Btn>
            ) : (
              <MissFooter
                allOk={allOk}
                explained={explained}
                onExplain={() => { setExplained(true); audio.ui("docOpen"); }}
                onRetry={() => { setFixMsg(""); setStage("fix"); }}
                onContinue={() => { if (allOk) dd.master("d9", false); else dd.acknowledge("d9"); finish("continue"); }}
                retryLabel="Try again to improve your score"
              />
            )}
        />
      )}
    </Win>
  );
}

/* ================================================================== D10 evidence (S1) + console (S6) */
interface D10Content {
  jordan: string;
  classes: DdOpt[];
  evidence: { id: string; label: string; scope: string; good: string }[];
  delta: string; policy: string;
  actions: DdOpt[];
  holdReasons: DdOpt[]; holdOwners: DdOpt[]; holdNext: DdOpt[];
  maya: string;
  crits: { id: string; label: string }[];
}
const EV_KEY = "cf1700:d10ev";

export function D10Evidence({ cfg, finish, onGuideChange }: StageProps) {
  const c = cfg.content as D10Content;
  const [m, setM] = useState<Record<string, string>>({});
  useGuide(onGuideChange, cfg.guide, "evidence");
  const ready = c.evidence.every((e) => m[e.id]);
  return (
    <Win app="AsterCloud Release Board" tag={"S1 \u00b7 Evidence folder"} clock={cfg.clock}>
      <div className="dd-split">
        <aside className="dd-side">
          <MissionHead n={10} time="16:45" title="What exactly are we releasing?" />
          <Bubble p={cfg.people.jordan}>{c.jordan}</Bubble>
          <div className="dd-cand">
            <b>Candidate R8</b>
            <small>{c.delta}</small>
          </div>
        </aside>
        <section className="dd-main">
          <div className="dd-main-h"><b>Release evidence</b><span className="dd-muted">Older is not automatically invalid: does it cover this artifact?</span></div>
          <div className="dd-evrows">
            {c.evidence.map((e) => (
              <div key={e.id} className={`dd-evrow${!m[e.id] ? " is-todo" : ""}`}>
                <div><b>{e.label}</b>{!m[e.id] && <Todo />}<small>Covers: {e.scope}</small></div>
                <Seg small options={c.classes} value={m[e.id]} onChange={(v) => setM((x) => ({ ...x, [e.id]: v }))} />
              </div>
            ))}
          </div>
          <div className="dd-actions">
            <span className={`dd-decided${ready ? " is-all" : ""}`}><b>{c.evidence.filter((e) => m[e.id]).length}</b> of {c.evidence.length} classified{ready ? " \u2713" : ""}</span>
            <Btn primary pulse={ready} disabled={!ready} onClick={() => { try { sessionStorage.setItem(EV_KEY, JSON.stringify(m)); } catch { /* ignore */ } audio.ui("docOpen"); finish("continue"); }}>File the evidence</Btn>
          </div>
        </section>
      </div>
    </Win>
  );
}

export function D10Console({ cfg, finish, onGuideChange, rec }: StageProps) {
  const c = cfg.content as D10Content;
  const [ev, setEv] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(EV_KEY) || "{}") as Record<string, string>;
    } catch {
      return {};
    }
  });
  const [act, setAct] = useState<string>();
  const [firstAct, setFirstAct] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [stage, setStage] = useState<"console" | "verify" | "hold" | "result" | "fix" | "fixed">("console");
  const [gates, setGates] = useState(0);
  const [hold, setHold] = useState<{ reason?: string; owner?: string; next?: string }>({});
  const [holdFirst, setHoldFirst] = useState<{ reason?: string; owner?: string; next?: string } | null>(null);
  const [holdMsg, setHoldMsg] = useState("");
  const [first, setFirst] = useState<DdCrit[] | null>(null);
  const [explained, setExplained] = useState(false);
  const [outcome, setOutcome] = useState<"verified" | "hold" | null>(null);
  const [fixMsg, setFixMsg] = useState("");
  const guideStage = stage === "fixed" || stage === "fix" ? "result" : stage;
  useGuide(onGuideChange, cfg.guide, guideStage);
  usePrefetch([c.maya]);
  const GATES = [
    "Full functional and security suite on R8: 48 passed",
    "Artifact identity: R8 build digest matches the reviewed source and configuration",
    "Sensitive-change review of R7 \u2192 R8: approved by Sam",
    "Dependency inventory: unchanged since review",
    "Incident: contained, recovery verified; impact review open, no production data involved (not blocking)",
  ];
  const depBlock = !!dd.get().dependencyBlocker;
  useEffect(() => {
    if (stage !== "verify") return;
    if (gates >= GATES.length) return;
    const t = window.setTimeout(() => {
      setGates((g) => g + 1);
      audio.ui("key");
    }, 700);
    return () => window.clearTimeout(t);
  }, [stage, gates, GATES.length]);

  const decide = () => {
    if (!act) return;
    const fa = firstAct ?? act;
    if (!firstAct) setFirstAct(act);
    if (act === "ship" || act === "model") {
      setBlocked(true);
      if (!firstAct) {
        rec("dd_d10_ship_unverified", act);
        dd.log("d10", "Tried to release R8 on R7's evidence; the enforced production gate blocked it.", "blocked");
      }
      audio.ui("error");
      return;
    }
    setBlocked(false);
    void fa;
    if (act === "verify") {
      setGates(0);
      setStage("verify");
    } else setStage("hold");
  };
  const conclude = (out: "verified" | "hold", rec5: boolean, decisionOk: boolean) => {
    const fa = firstAct ?? act;
    const cr = mkCrits(c.crits, {
      mismatch: ev.tests === "refresh",
      missing: ev.review === "refresh" && ev.smoke === "partial",
      blockers: fa === "verify" || fa === "hold",
      decision: (fa === "verify" || fa === "hold") && decisionOk,
      record: rec5,
    });
    const kept = dd.commitFirst("d10", cr, out === "verified" ? "Verified R8, then released it" : "Held R8 with an owner and a next step");
    setFirst(kept);
    rec("dd_d10_first", `${dd.missionScore("d10")}/10`);
    rec(out === "verified" ? "dd_release_verified" : "dd_release_held", "R8");
    dd.set({ outcome: out, hold: out === "hold" ? { reason: hold.reason ?? "", owner: hold.owner ?? "", next: hold.next ?? "" } : undefined });
    setOutcome(out);
    audio.ui("success");
    setStage("result");
  };
  const recordHold = () => {
    if (!hold.reason || !hold.owner || !hold.next) return;
    const hf = holdFirst ?? hold;
    if (!holdFirst) setHoldFirst({ ...hold });
    const good = hold.reason === "r8" && hold.owner === "qa" && hold.next === "suite";
    if (!good) {
      setHoldMsg("A responsible hold needs the real unresolved requirement, an accountable owner and a concrete next step.");
      audio.ui("error");
      if (!holdFirst) dd.log("d10", "First hold handoff was incomplete (correction needed).", "note");
      return;
    }
    conclude("hold", hf.owner === "qa" && hf.next === "suite", hf.reason === "r8");
  };
  const evOk = c.evidence.every((e) => ev[e.id] === e.good);
  const allOk = !!first && first.every((x) => x.ok);
  const fixEv = () => {
    if (!evOk) {
      setFixMsg("Some evidence cards still need a fix.");
      audio.ui("error");
      return;
    }
    dd.log("d10", "Matched every evidence card to the candidate it covers.", "corrected");
    audio.ui("success");
    setStage("fixed");
  };

  return (
    <Win app={"Release console \u00b7 OrbitDesk 4.8"} tag="S6" clock={cfg.clock}>
      {stage === "console" && (
        <div className="dd-split">
          <aside className="dd-side">
            <MissionHead n={10} time="16:52" title="Release decision" />
            <div className="dd-badges">
              <div className="dd-badge is-cand">Candidate R8</div>
              <div className="dd-badge is-green">{"\u2713"} Last full verification: R7</div>
            </div>
            <p className="dd-note">{c.policy}</p>
            {depBlock && <Consequence tone="risk" title="Open blocker: unreviewed dependency">Mission 4 left an unreviewed package in the candidate.</Consequence>}
          </aside>
          <section className="dd-main">
            <b className="dd-sub">What do you do with R8?</b>
            <Choice options={c.actions} value={act} onChange={(v) => { setAct(v); setBlocked(false); }} />
            {blocked && (
              <Consequence tone="risk" title={"Release request blocked \u00b7 Current candidate lacks required evidence"}>
                The protected production gate refused R8: its tests and review cover R7, not R8. Nothing was deployed. Choose to verify R8 or to hold it, then submit again.
              </Consequence>
            )}
            <div className="dd-actions">
              <Btn primary pulse={!!act && !blocked} disabled={!act || blocked} onClick={decide}>Submit decision</Btn>
            </div>
          </section>
        </div>
      )}
      {stage === "verify" && (
        <div className="dd-pad">
          <MissionHead n={10} time="16:55" title="Verifying candidate R8" />
          <ul className="dd-gates">
            {GATES.map((g, i) => (
              <li key={g} className={i < gates ? "is-ok" : "is-wait"}>
                <span aria-hidden="true">{i < gates ? "\u2713" : "\u2026"}</span> {g}
              </li>
            ))}
          </ul>
          <div className="dd-actions">
            <Btn primary pulse={gates >= GATES.length} disabled={gates < GATES.length} onClick={() => conclude("verified", true, true)}>Authorize verified release</Btn>
          </div>
        </div>
      )}
      {stage === "hold" && (
        <div className="dd-pad">
          <MissionHead n={10} time="16:55" title="Responsible hold: hand-off" />
          <div className="dd-form">
            <div><b>Candidate</b><span>R8</span></div>
            <div><b>Reason</b><Choice options={c.holdReasons} value={hold.reason} onChange={(v) => setHold((h) => ({ ...h, reason: v }))} /></div>
            <div><b>Owner</b><Seg small options={c.holdOwners} value={hold.owner} onChange={(v) => setHold((h) => ({ ...h, owner: v }))} /></div>
            <div><b>Next action</b><Seg small options={c.holdNext} value={hold.next} onChange={(v) => setHold((h) => ({ ...h, next: v }))} /></div>
            <div><b>Reassess when</b><span>The evidence is available</span></div>
          </div>
          <div className="dd-actions">
            {holdMsg && <span className="dd-warnline">{holdMsg}</span>}
            {holdMsg && <Btn onClick={() => { setHold({ reason: "r8", owner: "qa", next: "suite" }); setHoldMsg(""); audio.ui("success"); }}>Fill in the recommended hand-off</Btn>}
            <Btn primary pulse={!!(hold.reason && hold.owner && hold.next)} disabled={!(hold.reason && hold.owner && hold.next)} onClick={recordHold}>Record hold</Btn>
          </div>
        </div>
      )}
      {(stage === "result" || stage === "fixed") && first && (
        <MissionResult
          n={10}
          time="17:00"
          title={outcome === "verified" ? "Verified Release" : "Responsible Hold"}
          crits={first}
          outcomes={[
            outcome === "verified"
              ? { tone: "ok", title: "Required checks passed for this release", body: "R8 deployed through the protected pipeline. Monitoring: error rate normal. Northwind receives its own report; Harbor stays isolated." }
              : { tone: "ok", title: "Release held; customers remain protected while verification continues", body: "Owner: Sam with the release manager. Next: full suite and sensitive-change review on R8." },
          ]}
          more={<Bubble p={cfg.people.jordan}>{outcome === "verified" ? "Great work. Shipping for the demo." : "Understood. We'll use the current version for the demo."}</Bubble>}
          maya={{ person: cfg.people.maya, text: c.maya, speak: stage === "result" }}
          footer={stage === "fixed" ? (
              <Btn primary pulse onClick={() => { dd.master("d10", true); finish("continue"); }}>Continue to 17:00</Btn>
            ) : (
              <MissFooter
                allOk={allOk}
                explained={explained || evOk}
                onExplain={() => { setExplained(true); audio.ui("docOpen"); }}
                onRetry={() => { setFixMsg(""); setStage("fix"); }}
                onContinue={() => { if (allOk) dd.master("d10", false); else dd.acknowledge("d10"); finish("continue"); }}
              />
            )}
        />
      )}
      {stage === "fix" && (
        <div className="dd-pad">
          <MissionHead n={10} time="17:00" title="Match the evidence to R8" />
          <div className="dd-evrows">
            {c.evidence.map((e) => (
              <div key={e.id} className={`dd-evrow${ev[e.id] !== e.good ? " is-fix" : ""}`}>
                <div><b>{e.label}</b>{ev[e.id] !== e.good && <FixTag />}<small>Covers: {e.scope}</small></div>
                <Seg small options={c.classes} value={ev[e.id]} onChange={(v) => setEv((x) => ({ ...x, [e.id]: v }))} />
              </div>
            ))}
          </div>
          <div className="dd-actions">
            {fixMsg && <span className="dd-warnline">{fixMsg}</span>}
            <KeepInstead onClick={() => { setExplained(true); setFixMsg(""); setStage("result"); }} />
            <Btn primary pulse onClick={fixEv}>Save the evidence</Btn>
          </div>
        </div>
      )}
    </Win>
  );
}

/* ================================================================== 17:00 replay (S1) */
interface ReplayContent {
  maya: string;
  missions: { id: string; time: string; title: string }[];
  transfer: { feature: string; options: { id: string; label: string; ok: boolean }[]; explain: string };
  takeaways: Record<string, [string, string]>;
  crits: Record<string, { id: string; label: string }[]>;
}

export function Replay({ cfg, finish, onGuideChange }: StageProps) {
  const c = cfg.content as ReplayContent;
  const s = dd.get();
  const [tab, setTab] = useState<"day" | "take" | "transfer">("day");
  const [page, setPage] = useState(0);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [checked, setChecked] = useState(false);
  useGuide(onGuideChange, cfg.guide, "replay");
  usePrefetch([c.maya]);
  const route = dd.route();
  const score = route.reduce((sum, m) => sum + (dd.missionScore(m) ?? 0), 0);
  const maxScore = route.length * 10;
  const mastered = route.filter((m) => s.missions[m]?.mastered).length;
  const corrected = route.filter((m) => s.missions[m]?.mastered && s.missions[m]?.corrected).length;
  const mast = { mastered, corrected };
  const outcome = s.outcome === "verified" ? "Verified Release" : s.outcome === "hold" ? "Responsible Hold" : route.includes("d10") ? "Not decided yet" : "Not in your route";
  const per = 3;
  const pages = Math.ceil(c.missions.length / per);
  const ranked = route
    .map((id) => ({ id, sc: dd.missionScore(id) ?? 10 }))
    .sort((a, b) => a.sc - b.sc);
  const toQuiz = () => {
    // The final check only asks about missions in the learner's route.
    // Hosted courses keep every question: the server grades the full seeded
    // quiz and counts an unanswered question as wrong.
    if (route.length < MISSION_ORDER.length && !getHostConfig().hosted) {
      const st = useSim.getState();
      const sc = st.scenario;
      if (sc) {
        const qb = sc.beats.find((b) => b.type === "quiz");
        const all = qb?.quiz ?? [];
        const kept = all.filter((q) => route.includes(DD_QUIZ_TOPIC[q.id] ?? ""));
        if (kept.length && kept.length < all.length) {
          useSim.setState({ scenario: { ...sc, beats: sc.beats.map((b) => (b.type === "quiz" ? { ...b, quiz: kept } : b)) } });
        }
      }
    }
    finish("continue");
  };
  const weak = ranked.filter((r) => r.sc < 10).slice(0, 3);
  const strong = ranked.filter((r) => r.sc >= 10).reverse();
  const picks = [...weak.map((w) => ({ id: w.id, text: c.takeaways[w.id]?.[0] ?? "", kind: "improve" })), ...strong.map((w) => ({ id: w.id, text: c.takeaways[w.id]?.[1] ?? "", kind: "keep" }))].slice(0, 3);

  return (
    <Win app="AsterCloud Release Board" tag={"S1 \u00b7 17:00"} clock="17:00">
      <div className="dd-replay">
        <div className="dd-scores">
          <div><span>First-attempt decision score</span><b>{score} / {maxScore}</b>{route.length < MISSION_ORDER.length && <small>{route.length} missions in your route</small>}</div>
          <div><span>Demonstrated mastery</span><b>{mast.mastered} / {route.length}</b><small>{mast.corrected ? `${mast.corrected} after guided corrections` : "all first time"}</small></div>
          <div><span>Release outcome</span><b className="is-out">{outcome}</b></div>
        </div>
        <div className="dd-tabs" role="tablist">
          {([["day", "Your workday"], ["take", "Your three takeaways"], ["transfer", "Try a new feature"]] as [typeof tab, string][]).map(([id, l]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? "is-on" : ""} onClick={() => { audio.click(); setTab(id); }}>{l}</button>
          ))}
        </div>
        {tab === "day" && (
          <>
            <div className="dd-rcards">
              {c.missions.slice(page * per, page * per + per).map((m, i) => {
                const st = s.missions[m.id];
                const sc = dd.missionScore(m.id);
                const hist = s.history.filter((h) => h.mission === m.id);
                return (
                  <div key={m.id} className="dd-rcard">
                    <span className="dd-kicker">Mission {page * per + i + 1} {"\u00b7"} {m.time}</span>
                    <b>{m.title}</b>
                    <small className="dd-muted">{st?.first?.summary ?? "Not attempted"}</small>
                    <div className="dd-rmeta">
                      <span>{sc == null ? "\u2013" : `${sc} / 10`}</span>
                      <span className={st?.mastered ? (st.corrected ? "is-corr" : "is-ok") : st?.acknowledged ? "is-kept" : "is-muted"}>{st?.mastered ? (st.corrected ? "Corrected during training" : "Correct first time") : st?.acknowledged ? "Reviewed, kept result" : route.includes(m.id) ? "Not completed" : "Not in your route"}</span>
                    </div>
                    {hist.length > 0 && <ul className="dd-hist">{hist.map((h) => <li key={h.text} className={`is-${h.kind}`}>{h.text}</li>)}</ul>}
                  </div>
                );
              })}
            </div>
            <div className="dd-actions">
              <Btn small disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Btn>
              <span className="dd-muted">{page + 1} / {pages}</span>
              <Btn small disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>Next</Btn>
            </div>
          </>
        )}
        {tab === "take" && (
          <div className="dd-takes">
            <ol>
              {picks.map((p) => (
                <li key={p.id} className={`is-${p.kind}`}>{p.text}</li>
              ))}
            </ol>
            <Maya p={cfg.people.maya} text={c.maya} compact />
            <small className="dd-muted">The training score shows your decisions in this exercise. It is not a prediction of real-world risk.</small>
          </div>
        )}
        {tab === "transfer" && (
          <div className="dd-transfer">
            <b>{c.transfer.feature}</b>
            <p className="dd-muted">Which checks does it need? (Not scored.)</p>
            <div className="dd-toggles">
              {c.transfer.options.map((o) => (
                <button key={o.id} type="button" role="checkbox" aria-checked={!!picked[o.id]} className={`dd-check${picked[o.id] ? " is-on" : ""}${checked ? (o.ok ? " is-good" : picked[o.id] ? " is-bad" : "") : ""}`} onClick={() => { audio.click(); setPicked((m) => ({ ...m, [o.id]: !m[o.id] })); }}>
                  <span className="dd-box" aria-hidden="true">{picked[o.id] ? "\u2713" : ""}</span>{o.label}{checked && <em>{o.ok ? " (essential)" : " (not a control)"}</em>}
                </button>
              ))}
            </div>
            {checked ? <p className="dd-note">{c.transfer.explain}</p> : <Btn small onClick={() => setChecked(true)}>Check my answer</Btn>}
          </div>
        )}
        <footer className="dd-foot">
          <small className="dd-muted">Corrections are shown, never erased. A replay of the course cannot overwrite this attempt.</small>
          <Btn primary pulse onClick={toQuiz}>Continue to the final check</Btn>
        </footer>
      </div>
    </Win>
  );
}
